import {
    useState
} from "react";
import {
    PropsTable
} from "./components/shared";
import {
    useDocsI18n
} from "../i18n";
import type {
    DocsTocItem
} from "../components/toc";
import {
    Switch
} from "ncore-ui-kit";
import {
    PageHeader,
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../components/content";
import Anatomy from "../components/anatomy";

const USAGE_CODE = `const [isWifiOn, setIsWifiOn] = useState(true);

<Switch
    subTitle="Join known networks automatically"
    onCheckedChange={setIsWifiOn}
    isChecked={isWifiOn}
    title="Wi-Fi"
/>`;

const SIZE_CODE = `const [isOn, setIsOn] = useState(true);

<Switch
    onCheckedChange={setIsOn}
    title="Notifications"
    isChecked={isOn}
    size="small"
/>`;

export const getSwitchToc = (t: (key: string) => string): Array<DocsTocItem> => [
    {
        title: t("switch.overviewTitle"),
        id: "overview"
    },
    {
        title: t("switch.anatomyTitle"),
        id: "anatomy"
    },
    {
        title: t("switch.sizeTitle"),
        id: "sizes"
    },
    {
        title: t("switch.sizesTitle"),
        id: "measurements"
    },
    {
        title: t("switch.settingsTitle"),
        id: "settings-list"
    },
    {
        title: t("switch.disabledTitle"),
        id: "disabled"
    },
    {
        title: t("switch.propsTitle"),
        id: "props"
    }
];

const SwitchPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isWifiOn,
        setIsWifiOn
    ] = useState(true);

    const [
        settings,
        setSettings
    ] = useState({
        notifications: true,
        location: false,
        sync: true
    });

    const [
        sizeStates,
        setSizeStates
    ] = useState({
        medium: true,
        small: true
    });

    const toggleSize = (key: keyof typeof sizeStates) => {
        setSizeStates((current) => ({
            ...current,
            [key]: !current[key]
        }));
    };

    const toggleSetting = (key: keyof typeof settings) => {
        setSettings((current) => ({
            ...current,
            [key]: !current[key]
        }));
    };

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t("breadcrumb.components")
                },
                {
                    label: t("group.form")
                }
            ]}
            aside={t("switch.aside")}
            lead={t("switch.lead")}
            title="Switch"
        />
        <Section
            title={t("switch.overviewTitle")}
            id="overview"
        >
            <Paragraph>
                {t("switch.overviewText")}
            </Paragraph>
            <Preview
                caption={t("switch.previewCaption")}
            >
                <Switch
                    subTitle={t("switch.previewSubTitle")}
                    title={t("switch.previewTitle")}
                    onCheckedChange={setIsWifiOn}
                    isChecked={isWifiOn}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("switch.anatomyTitle")}
            note={t("switch.anatomyNote")}
            id="anatomy"
        >
            <Paragraph>
                {t("switch.anatomyText")}
            </Paragraph>
            <Anatomy
                states={[
                    {
                        node: <Switch
                            accessibilityLabel={t("switch.previewTitle")}
                            isChecked={false}
                        />,
                        label: t("switch.stateOff")
                    },
                    {
                        node: <Switch
                            accessibilityLabel={t("switch.previewTitle")}
                            isChecked={true}
                        />,
                        label: t("switch.stateOn")
                    }
                ]}
                caption={t("switch.anatomyCaption")}
                notes={[
                    t("switch.noteTravel"),
                    t("switch.noteThumb"),
                    t("switch.noteWidth")
                ]}
                names={[
                    t("switch.partTrack"),
                    t("switch.partThumb")
                ]}
                name="Switch"
            />
        </Section>
        <Section
            title={t("switch.sizeTitle")}
            id="sizes"
        >
            <Paragraph>
                {t("switch.sizeText")}
            </Paragraph>
            <Preview>
                <Switch
                    onCheckedChange={() => toggleSize("medium")}
                    title={t("switch.sizeMedium")}
                    isChecked={sizeStates.medium}
                    size="medium"
                />
                <Switch
                    onCheckedChange={() => toggleSize("small")}
                    title={t("switch.sizeSmall")}
                    isChecked={sizeStates.small}
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={SIZE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("switch.sizesTitle")}
            id="measurements"
        >
            <Paragraph>
                {t("switch.sizesText")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        t("switch.measureTrackWidth"),
                        "36",
                        code("W = iconSizes.large × 2 − spacingXxs × 2")
                    ],
                    [
                        t("switch.measureTrackHeight"),
                        "20",
                        code("H = iconSizes.large")
                    ],
                    [
                        t("switch.measureThumb"),
                        "Ø 16",
                        code("H − spacingXxs × 2")
                    ],
                    [
                        t("switch.measureInset"),
                        "2",
                        code("spacingXxs")
                    ],
                    [
                        t("switch.measureTravel"),
                        "16",
                        code("W − H")
                    ],
                    [
                        t("switch.measureRadius"),
                        t("switch.measureRadiusValue"),
                        code("radiuses.full")
                    ]
                ]}
                headers={[
                    t("switch.measurePart"),
                    t("switch.measureValue"),
                    t("switch.measureSource")
                ]}
            />
        </Section>
        <Section
            title={t("switch.settingsTitle")}
            id="settings-list"
        >
            <Paragraph>
                {t("switch.settingsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Switch
                    onCheckedChange={() => toggleSetting("notifications")}
                    subTitle={t("switch.settingNotificationsSub")}
                    title={t("switch.settingNotifications")}
                    isChecked={settings.notifications}
                    spreadBehaviour="stretch"
                />
                <Switch
                    onCheckedChange={() => toggleSetting("location")}
                    subTitle={t("switch.settingLocationSub")}
                    title={t("switch.settingLocation")}
                    isChecked={settings.location}
                    spreadBehaviour="stretch"
                />
                <Switch
                    onCheckedChange={() => toggleSetting("sync")}
                    subTitle={t("switch.settingSyncSub")}
                    title={t("switch.settingSync")}
                    isChecked={settings.sync}
                    spreadBehaviour="stretch"
                />
            </Preview>
        </Section>
        <Section
            title={t("switch.disabledTitle")}
            id="disabled"
        >
            <Paragraph>
                {t("switch.disabledText")}
            </Paragraph>
            <Preview>
                <Switch
                    title={t("switch.disabledOn")}
                    isDisabled={true}
                    isChecked={true}
                />
                <Switch
                    title={t("switch.disabledOff")}
                    isChecked={false}
                    isDisabled={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("switch.propsTitle")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("switch.propIsChecked"),
                        defaultValue: "false",
                        name: "isChecked",
                        type: "boolean"
                    },
                    {
                        description: t("switch.propIsDefaultChecked"),
                        name: "isDefaultChecked",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("switch.propOnCheckedChange"),
                        type: "(isChecked: boolean) => void",
                        name: "onCheckedChange"
                    },
                    {
                        description: t("switch.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("switch.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("switch.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("switch.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        description: t("switch.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("switch.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("switch.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("switch.propIsError"),
                        defaultValue: "false",
                        name: "isError",
                        type: "boolean"
                    },
                    {
                        description: t("switch.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("switch.propIsFlip"),
                        defaultValue: "false",
                        type: "boolean",
                        name: "isFlip"
                    },
                    {
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        description: t("switch.propSpreadBehaviour"),
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    }
                ]}
            />
        </Section>
    </Article>;
};
export default SwitchPage;
